import {
  ExtranetCustomerGroupDto,
  ExtranetCustomerGroupPouchLayoutDto,
} from '@pharmac-extranet/extranet-api'
import {
  CheckIcon,
  ExtranetContainer,
  ExtranetEditButton,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacText } from '@pharmac/ui'
import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { useExtranetPath } from '../../router/useExtranetPath'
import { api } from '../../services/HttpService'

type Props = {
  customerGroup: ExtranetCustomerGroupDto
  pouchLayoutId: number | null
}

export const CustomerGroupsListItemDetails = ({
  customerGroup,
  pouchLayoutId: pouchLayout,
}: Props) => {
  const navigate = useNavigate()
  const createLink = useExtranetPath()
  const { t } = useLanguage()
  const { customer } = useCustomerContext()

  const getCustomerGroupPouch = (
    customerId?: number
  ): UseQueryOptions<Array<ExtranetCustomerGroupPouchLayoutDto>> => ({
    queryFn: async () => await api.customers.getCustomerGroupPouchLayout(customerId as number),
    queryKey: ['__customerGroupPouch', customer?.id],
    enabled: typeof customerId === 'number',
  })

  const { data: customerGroupPouch } = useQuery(getCustomerGroupPouch(customer?.id))

  const layout = customerGroupPouch?.find((pouch) => pouch.id === Number(pouchLayout))

  return (
    <ExtranetContainer
      style={{ borderTop: '2px dashed var(--color-accent-4)' }}
      full
      paddingMedium
      flexRow
    >
      <ExtranetContainer half>
        <ExtranetContainer
          flexColumn
          paddingLargeRight
        >
          <PharmacText
            weight="bold"
            style={{ fontSize: '18px' }}
          >
            {t('extranet:customerGroupSettings.disclosure')}
          </PharmacText>
          {customerGroup.dataReleaseConsents.length > 0 ? (
            <>
              {customerGroup.dataReleaseConsents?.map((a) => (
                <PharmacText
                  key={a.key}
                  paddingMediumTop
                  style={{ fontSize: '18px' }}
                >
                  {a.name}
                </PharmacText>
              ))}
            </>
          ) : (
            <PharmacText
              paddingMediumTop
              style={{ fontSize: '18px' }}
            >
              {t('extranet:customerGroupSettings.dataRelease')}
            </PharmacText>
          )}
        </ExtranetContainer>
        <ExtranetContainer
          flexColumn
          paddingLargeRight
        >
          <PharmacText
            weight="bold"
            style={{ fontSize: '18px' }}
          >
            {t('extranet:customerGroupSettings.doseBagLayout')}
          </PharmacText>
          {layout ? (
            <PharmacText
              paddingMediumTop
              style={{ fontSize: '18px' }}
            >
              {layout?.name}
            </PharmacText>
          ) : (
            <PharmacText
              paddingMediumTop
              style={{ fontSize: '18px' }}
            >
              {t('extranet:customerGroupSettings.normalLayout')}
            </PharmacText>
          )}
        </ExtranetContainer>
        <ExtranetContainer flexColumn>
          <PharmacText
            weight="bold"
            style={{ fontSize: '18px' }}
            paddingMediumBottom
          >
            {t('extranet:customerGroupSettings.stickerlessGroup')}
          </PharmacText>
          {customerGroup.omitStickers && <CheckIcon icon="checkCircle" />}
        </ExtranetContainer>
      </ExtranetContainer>
      <ExtranetContainer
        half
        alignCenter
        justifyEnd
        flexRow
        paddingSmall
      >
        <ExtranetEditButton
          onClick={() =>
            navigate(
              createLink('oma-annos.customerGroupManagement.edit', { id: customerGroup.groupId })
            )
          }
        />
      </ExtranetContainer>
    </ExtranetContainer>
  )
}
